import { useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@garnet/ui';
import type { CopAnswer, CopPrompt } from '../../data/types.js';
import { AdvisorMark, Citations } from '../../components/advisor.js';

/* Garnet · AI Advisor — Copilot side panel.
   Available throughout the console, grounded on the org's data. It answers
   questions (every answer carries citations) and drafts on request. Asked to
   clear or pass something, it DECLINES and explains, then offers the path —
   the refusal is a feature, shown explicitly. */

type ThreadItem = { role: 'q'; text: string } | { role: 'a'; a: CopAnswer; q?: string };

function Answer({ a }: { a: CopAnswer }) {
  if (a.refusal) {
    return (
      <div className="cop-refusal">
        <div className="rf-tag">
          <Icon name="alert" size={12} /> {a.tag}
        </div>
        <div className="rf-body">{a.body}</div>
        <div className="rf-path">
          <div className="rp-lab">the path to clearing it</div>
          {a.path.map((s, i) => (
            <div key={i} className="rp-step">
              <span className="rp-n">{i + 1}</span>
              <span className="rp-t">
                <span className="who">{s.who} · </span>
                {s.t}
              </span>
            </div>
          ))}
        </div>
        {a.offer && (
          <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid var(--adv-line)', fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.5 }}>
            {a.offer}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="cop-a">
      <div className="am-head">
        <AdvisorMark size={13} />
        <span className="nm">Advisor</span>
      </div>
      <div className="ab">
        {a.blocks.map((b, i) => {
          if (b.type === 'p') return <p key={i}>{b.text}</p>;
          if (b.type === 'ev')
            return (
              <div key={i} className="ev">
                {b.text}
              </div>
            );
          if (b.type === 'draft')
            return (
              <div key={i} className="cop-draft">
                <div className="dl">{b.label}</div>
                <div className="dt">{b.text}</div>
                <div className="da">
                  <Button variant="primary" sm icon="check">
                    Use draft
                  </Button>
                  <Button variant="ghost" sm icon="exchange">
                    Edit
                  </Button>
                </div>
              </div>
            );
          return null;
        })}
        {a.cites && <Citations cites={a.cites} />}
      </div>
    </div>
  );
}

export function Copilot({
  prompts,
  answers,
  onClose,
}: {
  prompts: CopPrompt[];
  answers: Record<string, CopAnswer>;
  onClose: () => void;
}) {
  const [thread, setThread] = useState<ThreadItem[]>(() => {
    const why = answers.why;
    return why ? [{ role: 'a', a: why, q: 'Why did c6 flag on Gulf Medical?' }] : [];
  });
  const [text, setText] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [thread]);

  const ask = (id: string, label: string) => {
    const a = answers[id];
    const next: CopAnswer = a ?? {
      blocks: [
        {
          type: 'p',
          text: 'Grounded on Lench Procurement\'s data. Ask about a flag, what is missing to clear a supplier, or request a draft.',
        },
      ],
      cites: [{ kind: 'policy', k: 'org · Lench Procurement', s: 'scope' }],
    };
    setThread((t) => [...t, { role: 'q', text: label }, { role: 'a', a: next }]);
    setText('');
  };

  const onSend = () => {
    const v = text.trim();
    if (!v) return;
    const match = prompts.find((p) => p.label.toLowerCase() === v.toLowerCase());
    if (match) ask(match.id, match.label);
    else if (/clear|pass|approve|qualif|mark.*cleared|sign off/i.test(v)) ask('clear', v);
    else if (/draft/i.test(v)) ask('draft', v);
    else if (/missing|clear this supplier/i.test(v)) ask('missing', v);
    else if (/summar/i.test(v)) ask('summary', v);
    else if (/why|flag/i.test(v)) ask('why', v);
    else ask('__fallback', v);
  };

  return (
    <aside className="copilot">
      <div className="cop-head">
        <div className="ch-row">
          <span className="ch-title">
            <AdvisorMark size={16} />
            Copilot
          </span>
          <button className="cop-close" onClick={onClose} aria-label="Close copilot">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="ch-sub">
          <span className="gd" />
          grounded on Lench Procurement · REC-4471-v3
        </div>
      </div>

      <div className="cop-thread" ref={threadRef}>
        {thread.map((m, i) =>
          m.role === 'q' ? (
            <div key={i} className="cop-q">
              <div className="qb">{m.text}</div>
              <div className="qm">you · now</div>
            </div>
          ) : (
            <div key={i}>
              {m.q && (
                <div className="cop-q" style={{ marginBottom: 14 }}>
                  <div className="qb">{m.q}</div>
                  <div className="qm">you · now</div>
                </div>
              )}
              <Answer a={m.a} />
            </div>
          ),
        )}
      </div>

      <div className="cop-foot">
        <div className="cop-prompts">
          {prompts.map((p) => (
            <button key={p.id} className={'cop-prompt' + (p.guard ? ' guard' : '')} onClick={() => ask(p.id, p.label)}>
              {p.guard && <span style={{ marginRight: 5 }}>⚠</span>}
              {p.label}
            </button>
          ))}
        </div>
        <div className="cop-input">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSend()}
            placeholder="Ask, or request a draft…"
            aria-label="Ask the copilot"
          />
          <button className="cop-send" onClick={onSend} aria-label="Send">
            <Icon name="send" size={14} />
          </button>
        </div>
        <div className="cop-disc">
          Answers cite their grounding. The Advisor drafts and explains — it cannot clear, pass, or decide.
        </div>
      </div>
    </aside>
  );
}
